<template>
  <div ref="preview" class="topo-preview">
    <div class="topo-preview__mask"></div>
    <div ref="container" class="topo-preview__container"></div>
  </div>
</template>

<script setup>
import { useStationContext } from '@/views/station-monitor/detail/composables/useStationContext'
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue"
import { getTopology } from "@/api/device/topo"
import { ws_req } from "@/ws/wss-request"


import { Graph } from "@antv/x6"
import { registerNode } from './nodes'


// images
import payloadImg from '@/assets/images/topo/ic_payload.png'
import eblockImg from '@/assets/images/topo/ic_eblock.png'
import meterImg from '@/assets/images/topo/ic_meter.png'
import elinkImg from '@/assets/images/topo/ic_elink.png'
import cableImg from '@/assets/images/topo/ic_power_line.png'
import transformerImg from '@/assets/images/topo/ic_transformer.png'
import charge_pileImg from '@/assets/images/topo/ic_DC_pile.png'
import power_station_photovoltaicImg from '@/assets/images/topo/ic_Photovoltaic.png'
import power_station_storageImg from '@/assets/images/topo/ic_Energy.png'
import breakerImg from '@/assets/images/topo/ic_disconnect.png'
import contactorImg from '@/assets/images/topo/ic_connect.png'
import { useRealData } from '@/hooks/useRealData'


const { stationId, stationName, stationBaseData } = useStationContext()

const container = ref(null)
const preview = ref(null)
const loading = ref(false)



// const waitStationIdReady = (callback) => {
//   setTimeout(() => {
//     if (!stationId.value) {
//       waitStationIdReady(callback)
//     } else {
//       callback(stationId.value)
//     }
//   }, 200)
// }


watch(stationId, async () => {
  await drawGraph(stationId.value)
  // await updateRealData(false)
  updateOnce()
})

/**
 * @type {Graph} graph
 * 图实例
 */
let graph = null

onMounted(async () => {
  await nextTick()

  const el = container.value
  registerNode()

  graph = new Graph({
    container: el,
    resizing: true,
    panning: false,
    selecting: false,
    autoResize: false,
  })

  resizeGraph()

  // waitStationIdReady(async () => {
  //   await drawGraph(stationId.value)
  //   // await updateRealData(true)
  //   start()
  // })
  await drawGraph(stationId.value)
  // start()
})


function resizeGraph() {
  if (!preview.value || !container.value || !graph) {
    return
  }

  const { width, height } = preview.value.getBoundingClientRect()
  container.value.style.width = `${width}px`
  container.value.style.height = `${height}px`
  graph.resize(width, height)
}


async function drawGraph(stationId) {
  try {
    loading.value = true
    let r = await getTopology(stationId)
    let graphJSON = JSON.parse(r.data.stationTopology)
    // log('graphJSON', graphJSON)

    // 修改边的颜色为 #FA3A40
    for (const cell of graphJSON.cells) {
      if (cell.shape === 'edge') {
        if (cell.attrs && cell.attrs.line) {
          cell.attrs.line.stroke = '#FA3A40'
        }
      }
      // 修改母线颜色为 #FA3A40
      if (cell.shape === 'bus-node') {
        if (cell.attrs && cell.attrs.body) {
          cell.attrs.body.fill = '#FA3A40'
          cell.attrs.body.stroke = '#FA3A40'
        }
      }
      // 修改文本颜色
      if (cell.attrs) {
        // 标题颜色改为 #000000
        if (cell.attrs.label) {
          cell.attrs.label.fill = '#000000'
        }
        if (cell.attrs.text) {
          cell.attrs.text.fill = '#000000'
        }
        // 功率、电压、电流、功率因数颜色改为 #535B77
        const dataFields = ['power', 'voltage', 'current', 'q', 'soc', 'status', 'capacity', 'temp', 'temp_a', 'temp_b', 'temp_c']
        for (const field of dataFields) {
          if (cell.attrs[field]) {
            cell.attrs[field].fill = '#535B77'
          }
        }
        // 调整标题与图标的间距
        if (cell.attrs.label) {
          cell.attrs.label.refY = '-5'
        }
        // 调整数据字段与图标的间距
        for (const field of dataFields) {
          if (cell.attrs[field]) {
            cell.attrs[field].x = '40'
          }
        }
      }
    }

    setElementImage(graphJSON, 'payload', payloadImg)
    setElementImage(graphJSON, 'eblock', eblockImg)
    setElementImage(graphJSON, 'meter', meterImg)
    setElementImage(graphJSON, 'elink', elinkImg)
    // setElementImage(graphJSON, 'cable', cableImg)
    setElementImage(graphJSON, 'transformer', transformerImg)
    setElementImage(graphJSON, 'charge_pile', charge_pileImg)
    setElementImage(graphJSON, 'power_station_photovoltaic', power_station_photovoltaicImg)
    setElementImage(graphJSON, 'power_station_storage', power_station_storageImg)
    setElementImage(graphJSON, 'breaker', breakerImg)
    // setElementImage(graphJSON, 'contactactor', contactorImg)

    graph.fromJSON(graphJSON)
    resizeGraph()

    if (graphJSON.applyBg) {
      graph.drawBackground({
        color: graphJSON.bgColor,
        image: graphJSON.bgImg,
      })
    }

    // 缩放适配，确保内容完整显示
    setTimeout(() => {
      resizeGraph()
      graph.scaleContentToFit({
        padding: 20,
        maxScale: 1,
        minScale: 0.5,
        preserveAspectRatio: true,
      })
    }, 100)
  } catch (error) {
    console.warn(error)
  } finally {
    loading.value = false
  }
}


/**
 * 设置元素图片
 * @description 旧版系统在配置拓扑图时，会将元素的图片URL存储在data中。因此需要重新设置元素的图片URL
 * @param graphJSON 图数据
 * @param type 元素类型
 * @param img 图片URL
 */
function setElementImage(graphJSON, type, img) {
  for (const cell of graphJSON.cells) {
    if (cell.data.type == type) {
      cell.attrs.image['xlink:href'] = img
    }
  }
}



function findCellFromGraphByUUID(uuid) {
  for (const cell of graph.getCells()) {
    if (cell.data.uuid == uuid) {
      return cell
    }
  }
}


function isEmptyObject(obj) {
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false
    }
  }
  return true
}



// async function getRealData() {
//   const real_data = await ws_req.request('ws_device_diagram', {
//     stationId: stationId.value
//   })
//   console.log({ real_data })
//   return real_data
// }

function getMergedData(data) {
  const dt = []
  const addTo = (obj) => {
    Object.keys(obj).forEach(key => {
      let item = {
        key: key,
        value: obj[key]
      }
      dt.push(item)
    })
  }
  Object.keys(data).forEach(key => {
    addTo(data[key])
  })
  return dt
}

async function fillData(graph, data) {
  if (graph == null || isEmptyObject(data)) {
    return
  }
  for (const item of getMergedData(data)) {
    let { key, value } = item
    let cell = findCellFromGraphByUUID(key)
    if (!cell) {
      continue
    }

    let type = cell.data.type
    if (type == 'meter') {
      setMeterData(cell, value)
    } else if (type == 'eblock') {
      setEBlockData(cell, value)
    } else if (type == 'elink') {
      setELinkData(cell, value)
    } else if (type == 'transformer') {
      setTransformerData(cell, value)
    }
  }

  // 处理变压器周围的线
  for (const cell of graph.getCells()) {
    if (cell.data.type == 'transformer') {
      // 获取变压器的邻居节点 并找出类型为`meter`的节点 根据相邻的meter节点的p_total设置变压器连线的方向
      let neighbors = graph.getNeighbors(cell)
      for (const n of neighbors) {
        if (n.data.type == "meter") {
          const uuid = n.data.uuid
          const meterData = data.meter_data_list[uuid]
          setEdgeDirection(cell, meterData, "p_total")
        }
      }
    }
  }


}

/**
 * 设置输入输出边方向
 * @param {*} node 节点
 * @param {*} data 数据
 * @param {*} key 键
 * @param {*} isReverse 是否反转方向
 */
function setEdgeDirection(node, data, key, isReverse = false) {
  const incomeEdges = graph.getIncomingEdges(node)
  const outEdges = graph.getOutgoingEdges(node)

  const reverseEdges = (edges) => {
    if (Array.isArray(edges)) {
      edges.forEach(edge => {
        let value = data[key]
        // 值=0 时 停止箭头动画
        value == 0
          ? edge.attr("line/style/animation", "none")
          : edge.attr("line/style/animation", "ant-line 30s infinite linear")

        // 值<0 时 箭头设为反方向
        if (isReverse) {
          if (value < 0) {
            if (edge.attr("line/stroke") == '#36D600') {
              const source = edge.getSource()
              const target = edge.getTarget()
              edge.setSource(target)
              edge.setTarget(source)
              edge.attr("line/stroke", '#F5012C')
            }
          }
          else if (value > 0) {
            if (edge.attr("line/stroke") !== '#36D600') {
              const source = edge.getSource()
              const target = edge.getTarget()
              edge.setSource(target)
              edge.setTarget(source)
              edge.attr("line/stroke", '#36D600')
            }
          }

        } else {
          if (value < 0) {
            if (edge.attr("line/stroke") !== '#36D600') {
              const source = edge.getSource()
              const target = edge.getTarget()
              edge.setSource(target)
              edge.setTarget(source)
              edge.attr("line/stroke", '#36D600')
            }
          }
          else if (value > 0) {
            if (edge.attr("line/stroke") == '#36D600') {
              const source = edge.getSource()
              const target = edge.getTarget()
              edge.setSource(target)
              edge.setTarget(source)
              edge.attr("line/stroke", '#F5012C')
            }
          }
        }
      })
    }
  }

  reverseEdges(incomeEdges)
  reverseEdges(outEdges)

}



function fmtTxt(s) {
  return s != null ? s : '--'
}

function setMeterData(node, data) {
  // log('meter', data)
  setEdgeDirection(node, data, "p_total")

  node.attr("power/text", `功率：${fmtTxt(data.p_total)}kW`)
  node.attr("q/text", `功率因数：${fmtTxt(data.pf)}`)
  node.attr("voltage/text", `电压：${fmtTxt(data.u_a)}V`)
  node.attr("current/text", `电流：${fmtTxt(data.i_a)}A`)
}

function setEBlockData(node, data) {
  // log('eb', data)
  const EBStatus = {
    1: '停机',
    2: '待机',
    3: '充电',
    4: '放电',
    5: '降额',
    6: '告警',
    7: '故障',
  }
  const s = EBStatus[data.state_eblock] || '--'

  setEdgeDirection(node, data, "p_dc", true)

  node.attr("status/text", `状态：${s}`)
  node.attr("power/text", `功率：${fmtTxt(data.p_dc)}kW`)
  node.attr("voltage/text", `电压：${fmtTxt(data.u_dc)}V`)
  node.attr("current/text", `电流：${fmtTxt(data.i_dc)}A`)
  node.attr("soc/text", `SOC：${fmtTxt(data.soc)}%`)
}

function setELinkData(node, data) {
  // log('elk', data)
  setEdgeDirection(node, data, "p_total", true)
  node.attr("power/text", `功率：${fmtTxt(data.p_total)}KW`)
  node.attr("soc/text", `SOC：${fmtTxt(data.soc)}%`)
}

function setTransformerData(node, data) {
  // log('tfm', node, data)

  if (data == null && isEmptyObject(data)) {
    return
  }

  let dt = data
  // 昌得温控器当前温度为65535时，使用的是力得温控器，需要显示abc三相温度
  if (dt.tc_current_temp == 65535) {
    node.attr("capacity/text", `容量：${fmtTxt(node.data.capacity)}kVA`)
    node.attr("temp/text", `A相温度：${fmtTxt(dt.tc_phase_a_temp)}℃`)
    node.attr("temp_a/text", `B相温度：${fmtTxt(dt.tc_phase_b_temp)}℃`)
    node.attr("temp_b/text", `C相温度：${fmtTxt(dt.tc_phase_c_temp)}℃`)
    node.attr("temp_c/text", `D通道温度：${fmtTxt(dt.tc_channel_d_temp)}℃`)
  }
  else {
    node.attr("capacity/text", '')
    node.attr("temp/text", `容量：${fmtTxt(node.data.capacity)}kVA`)
    node.attr("temp_a/text", `温度：${fmtTxt(dt.tc_current_temp)}℃`)
    node.attr("temp_b/text", '')
    node.attr("temp_c/text", '')
  }

}


const { updateOnce } = useRealData({
  updateFunction: () => {
    return ws_req.request('ws_device_diagram', {
      stationId: stationId.value
    })
  },
  interval: 5000,
  onUpdate: (data) => {
    console.log('data', data)
    fillData(graph, data)
  }
})


</script>

<style scoped>
.topo-preview {
  position: relative;
  width: calc(100vw - 250px);
  height: calc(100vh - 200px);
  min-height: 0;
  overflow: auto;
}

.topo-preview__mask {
  position: absolute;
  z-index: 1;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  pointer-events: none;
}

.topo-preview__container {
  height: 100%;
  min-height: max-content;
}
</style>
